import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import type { ActivityFeedInformerItemFindInterface } from '@zaoblako/shared/modules/notification/dtos/informer/activity-feed-informer-item-find.dto';
import type { ActivityFeedInformerQueryInterface } from '@zaoblako/shared/modules/notification/dtos/informer/activity-feed-informer-query.dto';
import { NotificationKindEnum } from '@zaoblako/shared/modules/notification/enums/notification.kind.enum';
import { NotificationSubjectEnum } from '@zaoblako/shared/modules/notification/enums/notification.subject.enum';
import { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import { GlobalService } from '@zaoblako/web/core/services/global.service';
import { NotificationService } from '@zaoblako/web/core/services/notification/notification.service';
import { UnreadCountStorageService } from '@zaoblako/web/core/services/notification/unread-count-storage.service';
import { WebsocketNotificationService } from '@zaoblako/web/core/services/notification/websocket-notification.service';
import { PURCHASE_USERS } from '@zaoblako/web/modules/purchase/entities/purchase.users.mock';
import { ChangelogVersionComponent } from '@zaoblako/web/shared/components/changelog-version/changelog-version.component';
import { DocumentLinkComponent } from '@zaoblako/web/shared/components/document-link/document-link.component';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { NoDataSize } from '@zaoblako/web/shared/components/no-data/constants/no-data-types.enum';
import { NoDataComponent } from '@zaoblako/web/shared/components/no-data/no-data.component';
import { UserAvatarComponent } from '@zaoblako/web/shared/components/user-avatar/user-avatar.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { ConcatDateParamsPipe } from '@zaoblako/web/shared/pipes/concat-date-params.pipe';
import { OrderByPipe } from '@zaoblako/web/shared/pipes/order-by.pipe';
import { SafeHtmlPipe } from '@zaoblako/web/shared/pipes/safe-html.pipe';
import { TruncatePipe } from '@zaoblako/web/shared/pipes/truncate.pipe';
import { ConvertNotificationToLinkPipe } from '@zaoblako/web/shared/pipes/convert-notification-to-link.pipe';
import { NotificationTargetStatusEnum } from '@zaoblako/shared/modules/notification/enums/notification-target-status.enum';
import moment from 'moment-timezone';
import { BadgeModule } from 'primeng/badge';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { Subscription } from 'rxjs';

@Component({
  standalone: true,
  selector: 'crm-notification-list',
  templateUrl: './notification-list.component.html',
  imports: [
    CommonModule,
    BadgeModule,
    OverlayPanelModule,
    ButtonComponent,
    ConcatDateParamsPipe,
    ProgressSpinnerModule,
    OrderByPipe,
    UserAvatarComponent,
    TagModule,
    SafeHtmlPipe,
    RouterModule,
    TooltipModule,
    NoDataComponent,
    SpinnerComponent,
    TruncatePipe,
    DocumentLinkComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NotificationListComponent implements OnInit, OnDestroy {
  @Input() public informerType: NotificationKindEnum;
  @Input() public icon: string;
  @Input() public header: string;

  public queryParams: ActivityFeedInformerQueryInterface;
  public loading = false;
  public paginationLoading = false;
  public notifications: ActivityFeedInformerItemFindInterface[] = [];
  public countItemsNotification: number;
  public unreadCount = 0;
  public NoDataSize = NoDataSize.M;

  /** Подсветка иконки в нижней полосе, пока открыт этот информер */
  public informerOpen = false;

  private newWSMessageSubscription: Subscription;
  private wsNotificationReadSubscription: Subscription;
  private unreadCountSubscription: Subscription;
  private readNotificationSubscription: Subscription;
  private ref: DynamicDialogRef;

  public get NotificationTargetStatusEnum() {
    return NotificationTargetStatusEnum;
  }

  public constructor(
    private readonly websocketNotificationService: WebsocketNotificationService,
    private readonly unreadCountStorageService: UnreadCountStorageService,
    public readonly globalService: GlobalService,
    private readonly notificationService: NotificationService,
    private readonly formStoreService: FormStoreService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly dialogService: DialogService,
    private readonly router: Router
  ) {}

  public ngOnInit(): void {
    this.queryParams = {
      limit: 20,
      offset: 0,
      kinds: [this.informerType]
    };
    this.newWSMessageSubscription = this.websocketNotificationService.getNewMessage().subscribe({
      next: (socketMessage) => {
        if (socketMessage.message.kind === this.informerType) {
          this.websocketNotificationService.showNotificationPopup(socketMessage.message);
          this.unreadCount++;
          this.cdRef.markForCheck();
        }
      },
      error: (err) => console.error(err),
      complete: () => console.log('complete')
    });

    this.wsNotificationReadSubscription = this.websocketNotificationService.socketNotificationRead$.subscribe(
      (notification) => {
        if (notification.kind === this.informerType) {
          this.unreadCount--;
          this.cdRef.markForCheck();
        }
      }
    );

    this.unreadCountSubscription = this.notificationService.unreadCount$.subscribe((unreadCount) => {
      if (
        this.globalService.globalInfo.tenantInfo.subdomain === 'mvideo.zafrm.pro' &&
        this.informerType === NotificationKindEnum.PURCHASE
      ) {
        this.unreadCount = 6;
      } else if (
        this.globalService.globalInfo.tenantInfo.subdomain === 'mvideo.zafrm.pro' &&
        this.informerType === NotificationKindEnum.DEBT
      ) {
        this.unreadCount = 2;
      } else {
        this.unreadCount = unreadCount?.[this.informerType] ?? 0;
      }
      this.cdRef.markForCheck();
    });

    this.readNotificationSubscription = this.notificationService.eventReadNotification.subscribe((informerType) => {
      if (this.informerType === informerType) {
        this.unreadCount--;
        this.cdRef.markForCheck();
      }
    });
  }

  public ngOnDestroy() {
    this.newWSMessageSubscription?.unsubscribe();
    this.wsNotificationReadSubscription?.unsubscribe();
    this.unreadCountSubscription?.unsubscribe();
    this.readNotificationSubscription?.unsubscribe();
    this.ref?.close();
  }

  public onInformerShow(): void {
    this.informerOpen = true;
    this.loadNotifications();
    this.cdRef.markForCheck();
  }

  public onInformerHide(): void {
    this.informerOpen = false;
    this.queryParams.offset = 0;
    this.cdRef.markForCheck();
  }

  public loadNotifications({ concat } = { concat: false }): void {
    if (!concat) {
      this.loading = true;
      this.cdRef.markForCheck();
    }

    this.notificationService.getUnreadNotificationCount().subscribe((unreadCount) => {
      this.notificationService.unreadCount$.next(unreadCount);
    });
    const baseUrl = `${window.location.protocol}//${window.location.host}`;
    if (
      this.globalService.globalInfo.tenantInfo.subdomain === 'mvideo.zafrm.pro' &&
      this.informerType === NotificationKindEnum.PURCHASE
    ) {
      this.notifications = [
        <ActivityFeedInformerItemFindInterface>{
          ...(<ActivityFeedInformerItemFindInterface>{}),
          lastNotification: <any>{
            id: 1,
            created: moment().subtract(46, 'day').set({ hour: 15 }).toDate(),
            text: 'Вы назначены согласователем',
            user: <UserFindInterface>{
              id: 1,
              profile: { name: 'Колосов Дмитрий' },
              job: 'Менеджер поставщика'
            },
            subjectType: NotificationSubjectEnum.PURCHASE,
            subject: {
              title: 'Заявка № 112, Постащик - ООО "Разин"',
              link: `${baseUrl}/purchase/card/112`
            },
            kind: NotificationKindEnum.PURCHASE
          },
          notificationIds: [1]
        },
        {
          ...(<ActivityFeedInformerItemFindInterface>{}),
          lastNotification: <any>{
            id: 2,
            created: moment().subtract(5, 'day').set({ hour: 13, minutes: 23, second: 1 }).toDate(),
            text: 'Направляю Вам на согласование проект договора поставки',
            user: PURCHASE_USERS.find((user) => user.id === 21),
            subjectType: NotificationSubjectEnum.PURCHASE,
            subject: {
              link: `${baseUrl}/purchase/card/112`,
              title: 'Заявка № 112, Постащик - ООО "Разин"'
            },
            kind: NotificationKindEnum.PURCHASE
          },
          notificationIds: [2]
        },
        {
          ...(<ActivityFeedInformerItemFindInterface>{}),
          notificationIds: [3],
          lastNotification: <any>{
            id: 3,
            created: moment().subtract(5, 'day').set({ hour: 17, minutes: 34, second: 43 }).toDate(),
            text: 'Аудио файл',
            user: <UserFindInterface>{
              profile: { name: 'ФРМ' },
              job: 'Бот'
            },
            subjectType: NotificationSubjectEnum.PURCHASE,
            subject: {
              link: `${baseUrl}/purchase/card/112`,
              title: 'Заявка № 112, Постащик - ООО "Разин"'
            },
            kind: NotificationKindEnum.PURCHASE
          }
        },
        {
          ...(<ActivityFeedInformerItemFindInterface>{}),
          notificationIds: [4],
          lastNotification: <any>{
            id: 4,
            created: moment().subtract(4, 'day').set({ hour: 17, minutes: 6, second: 35 }).toDate(),
            text: 'Спасибо, получил.\nОтвет дам в течении 2-х рабочих дней',
            user: <UserFindInterface>{
              profile: { name: 'Колосов Дмитрий' },
              job: 'Менеджер поставщика'
            },
            subjectType: NotificationSubjectEnum.PURCHASE,
            subject: {
              link: `${baseUrl}/purchase/card/112`,
              title: 'Заявка № 112, Постащик - ООО "Разин"'
            },
            kind: NotificationKindEnum.PURCHASE
          }
        },
        {
          ...(<ActivityFeedInformerItemFindInterface>{}),
          notificationIds: [5],
          lastNotification: <any>{
            id: 5,
            created: moment().subtract(2, 'day').set({ hour: 19, minutes: 54, second: 1 }).toDate(),
            text: 'Добрый день! Высылаю протокол разногласий. Жду обратную связь',
            user: <UserFindInterface>{
              profile: { name: 'Колосов Дмитрий' },
              job: 'Менеджер поставщика'
            },
            subjectType: NotificationSubjectEnum.PURCHASE,
            subject: {
              link: `${baseUrl}/purchase/card/112`,
              title: 'Заявка № 112, Постащик - ООО "Разин"'
            },
            kind: NotificationKindEnum.PURCHASE
          }
        },
        {
          ...(<ActivityFeedInformerItemFindInterface>{}),
          notificationIds: [6],
          lastNotification: {
            id: 6,
            created: moment().subtract(1, 'day').set({ hour: 10, minutes: 45, second: 36 }).toDate(),
            text: 'Принято, передаю юристам',
            user: PURCHASE_USERS.find((user) => user.id === 21),
            subjectType: NotificationSubjectEnum.PURCHASE,
            subject: {
              link: `${baseUrl}/purchase/card/112`,
              title: 'Заявка № 112, Постащик - ООО "Разин"'
            },
            kind: NotificationKindEnum.PURCHASE
          }
        }
      ];
      this.countItemsNotification = 6;
      this.loading = false;
      this.paginationLoading = false;
      this.cdRef.markForCheck();
    } else if (
      this.globalService.globalInfo.tenantInfo.subdomain === 'mvideo.zafrm.pro' &&
      this.informerType === NotificationKindEnum.DEBT
    ) {
      this.notifications = [
        {
          ...(<ActivityFeedInformerItemFindInterface>{}),
          notificationIds: [7],
          lastNotification: <any>{
            id: 7,
            created: moment().subtract(43, 'day').set({ hour: 10, minutes: 45, second: 35 }).toDate(),
            text: 'Товар может закончиться на 24 дней раньше, возможные потери',
            subjectType: NotificationSubjectEnum.PURCHASE,
            subject: {
              link: `${baseUrl}/purchase/card/112`,
              title: 'Заявка № 112, Постащик - ООО "Разин"'
            },
            kind: NotificationKindEnum.DEBT
          }
        },
        {
          ...(<ActivityFeedInformerItemFindInterface>{}),
          notificationIds: [8],
          lastNotification: {
            id: 8,
            created: moment().subtract(43, 'day').set({ hour: 10, minutes: 45, second: 36 }).toDate(),
            text: 'Маржа 2% ниже запланированной',
            subjectType: NotificationSubjectEnum.PURCHASE,
            subject: {
              link: `${baseUrl}/purchase/card/112`,
              title: 'Заявка № 112, Постащик - ООО "Разин"'
            },
            kind: NotificationKindEnum.DEBT
          }
        }
      ];
      this.countItemsNotification = 2;
      this.loading = false;
      this.paginationLoading = false;
      this.cdRef.markForCheck();
    } else {
      this.websocketNotificationService.findList(this.queryParams).subscribe({
        next: (notifications) => {
          this.notifications = concat ? this.notifications.concat(notifications.items) : notifications.items;
          this.countItemsNotification = notifications.count;
          this.loading = false;
          this.paginationLoading = false;
          this.cdRef.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.cdRef.markForCheck();
        }
      });
    }
  }

  public read(notification: ActivityFeedInformerItemFindInterface): void {
    if (this.globalService.globalInfo.tenantInfo.subdomain === 'mvideo.zafrm.pro') {
      return;
    }

    if (
      notification?.lastNotification?.subjectType === NotificationSubjectEnum.CHANGELOG &&
      notification?.lastNotification?.text
    ) {
      this.showChangelogModal(notification);
    }

    this.websocketNotificationService.readNotification({ notificationIds: notification.notificationIds }).subscribe({
      next: () => {
        this.notifications.splice(this.notifications.indexOf(notification), 1);
        this.unreadCount -= notification.notificationIds?.length ?? 0;
        this.unreadCountStorageService.setParam({
          ...this.notificationService.unreadCount$.value,
          [this.informerType]: this.unreadCount
        });
        this.cdRef.markForCheck();
      }
    });
  }

  public onScrollEnd(target: HTMLElement): void {
    if (target.offsetHeight + Math.ceil(target.scrollTop) >= target.scrollHeight) {
      if (this.notifications.length < this.countItemsNotification && !this.loading && !this.paginationLoading) {
        this.paginationLoading = true;
        this.queryParams.offset += this.queryParams.limit;
        this.loadNotifications({ concat: true });
      }
    }
  }

  public showChangelogModal(changelog: ActivityFeedInformerItemFindInterface) {
    this.ref = this.dialogService.open(ChangelogVersionComponent, {
      header: 'История версий',
      contentStyle: { overflow: 'auto', padding: 0, 'border-radius': '1.5rem' },
      styleClass: 'xl:w-6 lg:w-8 md:w-10 w-full text-center border-round-3xl z-4',
      style: { 'min-width': '200px', 'max-width': '850px' },
      focusOnShow: false,
      showHeader: false,
      data: {
        changelog: changelog?.lastNotification
      }
    });
  }

  public openNotificationCard(notification: ActivityFeedInformerItemFindInterface) {
    if (
      [NotificationSubjectEnum.MAILING_TEMPLATE, NotificationSubjectEnum.MAILING].includes(
        notification.lastNotification?.subjectType
      )
    ) {
      const formParams = this.formStoreService.extractFormParams(notification.lastNotification?.subject?.link);
      this.formStoreService.editForm(formParams.formType, formParams.formId, {
        headerText: formParams.formHeaderText,
        widthSize: formParams.formWidthSize,
        isNotShowDelete: !formParams.formIsShowDelete,
        isShowShare: formParams.formIsShowShare,
        typeZIndex: formParams.formTypeZIndex
      });
    } else {
      this.router
        .navigate([new ConvertNotificationToLinkPipe().transform(notification.lastNotification)])
        .catch(console.error);
    }
  }
}
